/**
 * Text measurement + wrapping for in-figure labels.
 *
 * Plotly never wraps a title, subtitle or annotation on its own: the text is one
 * SVG <text> run that overflows the figure and gets clipped by the viewport. The
 * only lever it offers is an explicit `<br>` in the string, so the wrapping has
 * to be computed here, from the width actually available in pixels.
 *
 * Widths come from a canvas 2D context, which measures the real glyphs of the
 * real font — the same font string handed to Plotly. When no canvas is available
 * (jsdom under vitest, SSR) it degrades to an average-glyph estimate: coarser,
 * but it keeps the wrapping deterministic and testable instead of throwing.
 */

/** Average glyph width as a fraction of the font size, for the no-canvas path. */
const AVG_GLYPH_EM = 0.52;

/** Plotly font spec — the shape of the tokens in ../theme/layout. */
export interface FontSpec {
  family: string;
  size: number;
  weight?: number;
}

/** CSS `font` shorthand for a token, as canvas `measureText` expects it. */
export function fontCss(font: FontSpec): string {
  return `${font.weight ?? 400} ${font.size}px ${font.family}`;
}

// One context reused across every measurement — creating a canvas per call is
// what makes naive measuring slow. `null` marks "no canvas here" (jsdom's
// getContext returns null, which is why the tests all take the estimate path).
let measureCtx: CanvasRenderingContext2D | null | undefined;

function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (measureCtx !== undefined) return measureCtx;
  let err: unknown;
  try {
    measureCtx = document.createElement('canvas').getContext('2d') ?? null;
  } catch (caught) {
    measureCtx = null;
    err = caught;
  }
  // Warn on the RESULT, not only on a throw: the common no-canvas case is
  // `getContext` returning null (jsdom, but also Firefox resistFingerprinting and
  // canvas-blocking extensions), which never throws — so a warning inside `catch`
  // alone never fired for the case that actually happens.
  if (measureCtx === null && import.meta.env.DEV) {
    console.warn(
      '[dashboard-kit] no canvas 2D available: in-figure text widths are ' +
        `ESTIMATED at ${AVG_GLYPH_EM}em per glyph, not measured. Line breaks and ` +
        'font fitting will be approximate.',
      err
    );
  }
  return measureCtx;
}

/** Weight Plotly renders a `<b>` run at, so it is measured at that weight too. */
const BOLD_WEIGHT = 700;

/** Plotly's inline markup. `<br>` is a line break and never reaches a measurement. */
const MARKUP_TAG = /<\/?[a-z][^>]*>/gi;
const BOLD_RUN = /<b>([\s\S]*?)<\/b>/gi;

/** Width of a run with no markup left in it. */
function measurePlain(text: string, font: FontSpec): number {
  if (!text) return 0;
  const ctx = getMeasureCtx();
  if (ctx) {
    ctx.font = fontCss(font);
    const w = ctx.measureText(text).width;
    // A context that measures everything as 0 (a stub, a detached canvas) is not
    // a measurement — fall through to the estimate rather than trust the 0.
    if (w > 0) return w;
  }
  return text.length * font.size * AVG_GLYPH_EM;
}

/**
 * Rendered width of `text` in px — the width Plotly will actually draw.
 *
 * Plotly's inline markup is NOT glyphs: measuring the raw string counted the 7
 * characters of `<b></b>` as visible, so `<b>cut_lp-LP:</b>` measured 126px
 * against the 73px it renders (1.7x). Conversely a `<b>` run draws bold but was
 * measured at weight 400, an error of the opposite sign — so widths were wrong in
 * both directions at once. Bold runs are measured at BOLD_WEIGHT and every other
 * tag is stripped before measuring.
 *
 * Falls back to an average-glyph estimate when no canvas is available; never throws.
 */
export function measureTextWidth(text: string, font: FontSpec): number {
  if (!text) return 0;
  let total = 0;
  let last = 0;
  for (const match of text.matchAll(BOLD_RUN)) {
    const at = match.index ?? 0;
    total += measurePlain(text.slice(last, at).replace(MARKUP_TAG, ''), font);
    total += measurePlain(match[1].replace(MARKUP_TAG, ''), { ...font, weight: BOLD_WEIGHT });
    last = at + match[0].length;
  }
  total += measurePlain(text.slice(last).replace(MARKUP_TAG, ''), font);
  return total;
}

/**
 * Break `text` into `<br>`-joined lines that each fit `maxWidthPx`, splitting on
 * spaces only. Returns the text untouched when it already fits, when the width
 * is unknown (<= 0, i.e. the container hasn't been measured yet), or when it
 * already carries explicit `<br>` breaks — a caller that hand-authored its line
 * breaks owns them.
 *
 * A single word longer than the budget is left whole rather than hyphenated: for
 * these labels (column names, `μ=0.343`) a mid-token break reads as corrupt data.
 * Such a line still overflows — the caller's remaining lever is a smaller font.
 */
export function wrapToWidth(text: string, maxWidthPx: number, font: FontSpec): string {
  if (!text || maxWidthPx <= 0 || text.includes('<br>')) return text;
  if (measureTextWidth(text, font) <= maxWidthPx) return text;

  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && measureTextWidth(candidate, font) > maxWidthPx) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines.join('<br>');
}

/**
 * Wrap every `<br>`-separated line of an already-multiline string (e.g. a stats
 * box, where each entry is its own line) so one long entry can't widen the whole
 * box. Splitting on the existing breaks first is what keeps the entries as
 * entries — `wrapToWidth` alone would refuse the string for containing `<br>`.
 */
export function wrapLines(text: string, maxWidthPx: number, font: FontSpec): string {
  if (!text || maxWidthPx <= 0) return text;
  return text
    .split('<br>')
    .map((line) => wrapToWidth(line, maxWidthPx, font))
    .join('<br>');
}

/**
 * Width of the widest unbreakable token in `text` — the floor on how narrow
 * wrapping can make it. Tokens are split on spaces and `<br>`.
 *
 * Needed because a stats entry like `mediana=0.295` carries no space, so no amount
 * of wrapping narrows it: if it exceeds its slot it reaches under the neighbouring
 * column's opaque background and its tail disappears (that is how the histogram
 * came to show "mediana=0."). Comparing this against the slot is what tells the
 * caller to reach for a smaller font instead.
 */
export function widestTokenWidth(text: string, font: FontSpec): number {
  let widest = 0;
  for (const token of text.split(/<br>|\s+/)) {
    if (!token) continue;
    widest = Math.max(widest, measureTextWidth(token, font));
  }
  return widest;
}

/**
 * Font size that makes `text`'s widest token fit `maxWidthPx`, never below
 * `minSize` and never above the token's own size. Returns `font.size` unchanged
 * when it already fits or the width is unknown.
 */
export function fitFontToWidth(
  text: string,
  maxWidthPx: number,
  font: FontSpec,
  minSize: number
): number {
  if (maxWidthPx <= 0) return font.size;
  const widest = widestTokenWidth(text, font);
  if (widest <= maxWidthPx || widest === 0) return font.size;
  return Math.max(minSize, Math.floor((font.size * maxWidthPx) / widest));
}

/**
 * Fit `text` into a box: wrap it to `maxWidthPx` and, if the resulting stack of
 * lines is taller than `maxHeightPx`, step the font down until it isn't (or the
 * floor is reached). Returns the chosen size together with the text wrapped AT
 * that size — the two must travel together, since a smaller font re-wraps into
 * fewer lines.
 *
 * Height is the constraint the width-only fit misses: wrapping a wide stats box
 * makes it TALLER, and on a fixed-height card that is how it ended up colliding
 * with the legend below the plot (measured: 120x45px on the scatter).
 * `lineHeight` is a multiple of the font size.
 */
export function fitTextToBox(
  text: string,
  maxWidthPx: number,
  maxHeightPx: number,
  font: FontSpec,
  minSize: number,
  lineHeight: number,
  boxPaddingPx = 0
): { size: number; text: string } {
  // Start from the width fit — a token wider than the slot already forces a size.
  const size0 = fitFontToWidth(text, maxWidthPx, font, minSize);
  // Wrap BEFORE any early return: returning a reduced size next to the original
  // unwrapped text would shrink the font while leaving the lines long — the worst
  // of both adjustments, and a silent break of this function's own pair contract.
  if (!Number.isFinite(maxWidthPx) || maxWidthPx <= 0) return { size: size0, text };
  let size = size0;
  let wrapped = wrapLines(text, maxWidthPx, { ...font, size });
  if (!Number.isFinite(maxHeightPx) || maxHeightPx <= 0) return { size, text: wrapped };

  // `boxPaddingPx` covers the chrome the caller draws around the text (an
  // annotation's borderpad on both sides plus its border), which is not part of
  // the line stack but does count against the budget.
  const tooTall = (s: number, w: string) =>
    w.split('<br>').length * s * lineHeight + boxPaddingPx > maxHeightPx;
  // `size` can start fractional (CHART_FONT.size is 12.5), so a plain `size -= 1`
  // steps 12.5 -> 11.5 -> 10.5 -> 9.5 -> 8.5 and lands BELOW a floor of 9. Clamp
  // every step and stop once the floor is reached, floor beating fit by design.
  while (size > minSize && tooTall(size, wrapped)) {
    size = Math.max(minSize, size - 1);
    wrapped = wrapLines(text, maxWidthPx, { ...font, size });
  }
  return { size, text: wrapped };
}

/** Test seam: forget the cached canvas context so a suite can swap it out. */
export function resetMeasureCache(): void {
  measureCtx = undefined;
}
